import { useState } from 'react'

import type { ReactElement } from 'react'
import type { FileWithPath } from 'react-dropzone'

import Button from '~/components/Buttons/Button'
import Dropzone from '~/components/Dropzone'
import FilePreview from '~/components/FilePreview'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'

import './styles.scss'

interface Props {
  existingScreenshots?: Record<string, string>
  onClose: VoidFunction
  onSubmit: (files: File[], remainingScreenshots?: Record<string, string>) => Promise<void>
}

const UploadScreenshotsModal = ({ existingScreenshots, onClose, onSubmit }: Props): ReactElement => {
  const [screenshots, setScreenshots] = useState(existingScreenshots)

  const [files, setFiles] = useState<FileWithPath[]>([])

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    setIsSubmitting(true)
    await onSubmit(files, screenshots)
    setIsSubmitting(false)
  }

  const handleDrop = (newFiles: FileWithPath[]) => {
    setFiles([...files, ...newFiles])
  }

  const handleRemoveScreenshot = (fileId: string) => {
    if (screenshots) {
      const { [fileId]: _, ...rest } = screenshots //eslint-disable-line
      setScreenshots(Object.values(screenshots).length > 0 ? rest : undefined)
    }
  }

  const handleRemove = (index: number) => {
    files.splice(index, 1)
    setFiles([...files])
  }

  return (
    <>
      <ModalHeader>Upload screenshots</ModalHeader>
      <ModalBody>
        <Dropzone
          accept={{ 'image/*': [] }}
          className="screenshots-dropzone"
          isLoading={false}
          multiple
          onDrop={handleDrop}
        />
        <div className="uploaded-files">
          {screenshots
            ? Object.entries(screenshots).map(([id, url], index) => (
                <FilePreview
                  className="small"
                  isEditable={true}
                  key={`screenshot-${index}`}
                  mimeType="image/*"
                  name={''}
                  onRemove={() => handleRemoveScreenshot(id)}
                  src={url}
                  withQualityIdentifier={false}
                />
              ))
            : null}
          {files.map((file, index) => (
            <FilePreview
              className="small"
              isEditable={true}
              key={`file-${index}`}
              mimeType={file.type}
              name={file.path || ''}
              onRemove={() => handleRemove(index)}
              src={URL.createObjectURL(file)}
              withQualityIdentifier={false}
            />
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={onClose} type="button">
          Cancel
        </Button>
        <Button disabled={isSubmitting || files.length === 0} onClick={handleSubmit}>
          Save
        </Button>
      </ModalFooter>
    </>
  )
}

export default UploadScreenshotsModal
